/**
 * @file ShopSwitcherList.tsx
 * @description Список кабинетов для переключателя магазинов (phase19.31,
 * SC19; uiux-design §2).
 *
 * Магазины сотрудника (монограмма, название, роль), текущий отмечен; поиск —
 * когда магазинов `SEARCH_MIN_SHOPS` и больше; внизу — «Кабинет платформы»,
 * если у пользователя есть такой доступ. Строки 48px (56 в режиме склада —
 * через высоту пунктов меню темы). Пустой поиск — понятная фраза.
 */

import { Check, LayoutDashboard, Search } from "lucide-react";
import { useState } from "react";

import type { AccountContext } from "../../auth/types";
import { Input } from "../ui/input";
import commoraMarkUrl from "../../assets/brand/commora-mark.svg";
import { ShopMonogram } from "./ShopMonogram";
import { cn } from "@/lib/utils";

/** С какого числа магазинов показывать поиск. */
export const SEARCH_MIN_SHOPS = 6;

interface Props {
  contexts: readonly AccountContext[];
  currentId: string | null;
  onPick: (ctx: AccountContext) => void;
}

const ROW = cn(
  "flex min-h-[var(--menu-item-h,3rem)] w-full items-center gap-3 rounded-md px-3 py-2 text-left",
  "outline-none transition-colors hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring",
);

export function ShopSwitcherList({ contexts, currentId, onPick }: Props) {
  const [query, setQuery] = useState("");
  const shops = contexts.filter((c) => c.contextType === "SHOP" && c.shop);
  const platform = contexts.find((c) => c.contextType === "PLATFORM") ?? null;
  const q = query.trim().toLocaleLowerCase("ru-RU");
  const visible = q ? shops.filter((c) => c.shop?.displayName.toLocaleLowerCase("ru-RU").includes(q)) : shops;

  return (
    <div className="flex flex-col gap-2">
      {shops.length >= SEARCH_MIN_SHOPS ? (
        <div className="relative">
          <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Найти магазин"
            aria-label="Найти магазин"
            className="pl-9"
          />
        </div>
      ) : null}
      <ul className="flex flex-col gap-0.5" aria-label="Магазины">
        {visible.map((ctx) => {
          const current = ctx.id === currentId;
          return (
            <li key={ctx.id}>
              <button type="button" className={ROW} aria-current={current ? "true" : undefined} onClick={() => onPick(ctx)}>
                <ShopMonogram shopId={ctx.shop?.id ?? ctx.id} name={ctx.shop?.displayName ?? ""} />
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="truncate text-sm font-medium text-on-surface">{ctx.shop?.displayName}</span>
                  <span className="truncate text-xs text-on-surface-variant">{ctx.role.displayName}</span>
                </span>
                {current ? <Check aria-label="Открыт сейчас" className="size-4 shrink-0 text-primary" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {visible.length === 0 ? (
        <p className="px-3 py-2 text-sm text-on-surface-variant">
          Магазин «{query.trim()}» не найден среди ваших кабинетов.
        </p>
      ) : null}
      {platform ? (
        <div className="border-t border-border pt-2">
          <button type="button" className={ROW} onClick={() => onPick(platform)}>
            <img src={commoraMarkUrl} alt="" aria-hidden width={32} height={32} className="shrink-0" />
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-sm font-medium text-on-surface">Кабинет платформы</span>
              <span className="truncate text-xs text-on-surface-variant">{platform.role.displayName}</span>
            </span>
            <LayoutDashboard aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
